import { useCallback, useEffect, useMemo, useState } from "react"
import {
  interactionFilters,
  summariseReactions,
  uniqueInteractionAuthors,
  type NoteInteractions,
} from "@/nostr/api/interactions"
import type { NostrEvent } from "@/nostr/types"
import { useAuth } from "@/state/AuthContext"
import { useNostr } from "@/state/NostrContext"
import { useRelays } from "@/state/RelayContext"
import { preferredLookupRelays } from "@/utils/url"

/** Compact counters shared by the note action bar. */
export interface EngagementResult {
  likes: number
  reposts: number
  replies: number
  myReaction?: string
  myRepost: boolean
  loading: boolean
}

export interface InteractionsResult {
  reactions: NostrEvent[]
  reposts: NostrEvent[]
  quotes: NostrEvent[]
  reactionAuthors: string[]
  repostAuthors: string[]
  summary: ReturnType<typeof summariseReactions>
  engagement: EngagementResult
  loading: boolean
  /** Adds a just-published reaction/repost locally so the tabs update instantly. */
  registerReaction: (event: NostrEvent) => void
  registerRepost: (event: NostrEvent) => void
}

const EMPTY: NoteInteractions & { loading: boolean } = {
  reactions: [],
  reposts: [],
  quotes: [],
  loading: false,
}

/**
 * Reactions, reposts and quotes of one note.
 *
 * A single subscription with three filters (one REQ per relay on the first few
 * lookup relays) powers the whole interaction bar and the detail tabs.
 */
export function useInteractions(noteId?: string): InteractionsResult {
  const { pool, store } = useNostr()
  const { pubkey } = useAuth()
  const { appRelays } = useRelays()
  const [state, setState] = useState<NoteInteractions & { loading: boolean }>(EMPTY)
  const relays = useMemo(() => preferredLookupRelays(appRelays, 3), [appRelays])

  useEffect(() => {
    if (!noteId) {
      setState(EMPTY)
      return
    }

    let cancelled = false
    const reactions = new Map<string, NostrEvent>()
    const reposts = new Map<string, NostrEvent>()
    const quotes = new Map<string, NostrEvent>()
    let flushTimer: ReturnType<typeof setTimeout> | undefined

    const flush = (loading: boolean) => {
      if (cancelled) return
      setState({
        reactions: [...reactions.values()],
        reposts: [...reposts.values()],
        quotes: [...quotes.values()],
        loading,
      })
    }

    setState((current) => ({ ...current, loading: true }))

    const subscription = pool.subscribe(interactionFilters(noteId), {
      relays,
      onEvent: (event) => {
        store.add(event)
        if (event.kind === 7) reactions.set(event.id, event)
        else if (event.kind === 6) reposts.set(event.id, event)
        else if (event.kind === 1 && event.tags.some((tag) => tag[0] === "q" && tag[1] === noteId)) {
          quotes.set(event.id, event)
        }
        if (!flushTimer) flushTimer = setTimeout(() => flush(false), 200)
      },
      onEose: () => {
        if (flushTimer) clearTimeout(flushTimer)
        flushTimer = undefined
        flush(false)
      },
    })

    return () => {
      cancelled = true
      if (flushTimer) clearTimeout(flushTimer)
      subscription.close()
    }
  }, [noteId, pool, store, relays])

  const summary = useMemo(() => summariseReactions(state.reactions, pubkey), [state.reactions, pubkey])
  const reactionAuthors = useMemo(() => uniqueInteractionAuthors(state.reactions), [state.reactions])
  const repostAuthors = useMemo(
    () => uniqueInteractionAuthors([...state.reposts, ...state.quotes]),
    [state.reposts, state.quotes],
  )

  const engagement = useMemo<EngagementResult>(
    () => ({
      likes: summary.total,
      reposts: state.reposts.length,
      replies: 0,
      myReaction: summary.mine,
      myRepost: Boolean(pubkey) && state.reposts.some((event) => event.pubkey === pubkey),
      loading: state.loading,
    }),
    [summary, state.reposts, state.loading, pubkey],
  )

  const registerReaction = useCallback((event: NostrEvent) => {
    setState((current) =>
      current.reactions.some((item) => item.id === event.id)
        ? current
        : { ...current, reactions: [event, ...current.reactions] },
    )
  }, [])

  const registerRepost = useCallback((event: NostrEvent) => {
    setState((current) =>
      current.reposts.some((item) => item.id === event.id)
        ? current
        : { ...current, reposts: [event, ...current.reposts] },
    )
  }, [])

  return {
    reactions: state.reactions,
    reposts: state.reposts,
    quotes: state.quotes,
    reactionAuthors,
    repostAuthors,
    summary,
    engagement,
    loading: state.loading,
    registerReaction,
    registerRepost,
  }
}
